import styled from "styled-components";
import { Modal } from "../../../design-system/components/composite/Modal/Modal";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin, fontWeights } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { controlSize } from "../../../design-system/tokens/controlSizes";

/**
 * F18: the dialog is 828px tall with 64px header and 68px footer, so the body
 * is 696px. The cap keeps it inside a short window; the form scrolls inside.
 */
const DELIVER_BODY_HEIGHT = "min(43.5rem, calc(100dvh - 12rem))";

export const Title = styled.h2`
  margin: 0;
  ${typographyMixin("heading", "h5")}
  font-weight: ${fontWeights.semibold};
  color: ${rawColors.text.highEmphasis};
`;

/** The preview pane is a fixed 560px aside beside the form (was 330px, too narrow for the email layout's 2-column grid). */
const DELIVER_PREVIEW_WIDTH = "35rem";

export const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${DELIVER_PREVIEW_WIDTH};
  gap: 0;
  align-items: stretch;
  width: 100%;
  height: ${DELIVER_BODY_HEIGHT};
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;

  & > * {
    min-width: 0;
    min-height: 0;
  }
`;

export const PreviewColumn = styled.div`
  padding: ${spacing[5]};
  border-left: 1px solid ${rawColors.stroke[200]};
  background-color: ${rawColors.grey[50]};
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
`;

export const CancelLink = styled.button`
  background: none;
  border: none;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${rawColors.text.highEmphasis};
  }
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: ${spacing[16]};
`;

export const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

export const SendErrorText = styled.p`
  margin: 0 0 ${spacing[3]};
  ${typographyMixin("caption", 3)}
  color: ${rawColors.accent.red500};
`;

export const FooterRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[4]};
`;

export const FooterLeft = styled.div`
  flex: 1;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[4]};
`;

export const FooterNote = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.primary[500]};

  &:hover {
    text-decoration: underline;
  }
`;

/** Outline pill ("Send a test"). */
export const TestDeliveryButton = styled(Button)`
  border-radius: ${borderRadius.full};
  height: ${controlSize.field.md};
  color: ${rawColors.text.highEmphasis};
  border-color: ${rawColors.stroke[200]};
  background-color: ${rawColors.structural.white};

  &:hover:not(:disabled) {
    background-color: ${rawColors.grey[50]};
  }
`;

/** Primary pill ("Schedule" / "Send now"). */
export const PrimaryPillButton = styled(Button)`
  border-radius: ${borderRadius.full};
  height: ${controlSize.field.md};
  background-color: ${rawColors.primary[500]};
  border-color: ${rawColors.primary[500]};
  color: ${rawColors.text.white};

  &:hover:not(:disabled) {
    background-color: ${rawColors.primary[600]};
    border-color: ${rawColors.primary[600]};
  }

  /* Keep the label legible when disabled. */
  &:disabled {
    color: ${rawColors.text.white};
  }
`;

/** Composer dialog shell: design radius 20 (Modal forwards className to its card). */
export const ComposerModal = styled(Modal)`
  border-radius: ${borderRadius[20]};
`;
